import React from "react";

import PropTypes from "prop-types";
import { FormattedMessage, intlShape } from "react-intl";
import { Field, reduxForm } from "redux-form";

import { Button } from "@ps-design-system/components/Button";
import { useAsyncLoadingFn } from "@ps-design-system/lib/hooks/use-async-loading-fn";

import {
  TAX_ID_FORM_NAME,
  VAT_ID_FORM_NAME,
  TAX_INFORMATION_TYPES,
} from "./tax-information.constants";
import { getTaxInformationMetaById } from "./tax-information.utils";
import { taxInformationFormValidation } from "./tax-information.validation";
import { TaxInformationInputField } from "./TaxInformationInput";

const TaxInformationForm = ({
  handleSubmit: reduxFormsHandleSubmit,
  taxInformationId,
  submitError,
  setSubmitError,
}) => {
  const [handleSubmit, isLoading] = useAsyncLoadingFn(async (...args) => {
    await reduxFormsHandleSubmit(...args);
  });

  const { shortNameMessageDescriptor: taxMessageDescriptor } =
    getTaxInformationMetaById(taxInformationId);

  return (
    <form onSubmit={handleSubmit}>
      <div className="tw-flex tw-flex-wrap tw-items-top tw-gap-8">
        <Field
          name="taxInformation"
          id="taxInformation"
          component={TaxInformationInputField}
          error={submitError}
          setError={setSubmitError}
          taxInformationId={taxInformationId}
          // Override styles to fit into legacy page
          style={{ borderRadius: "2px" }}
        />

        <Button
          type="submit"
          showSpinner={isLoading}
          disabled={isLoading}
          // Override styles to fit into legacy page
          style={{ borderRadius: "2px" }}
        >
          <FormattedMessage
            id="hosting.tax_information_section.save_tax_id"
            defaultMessage="Save {taxIdShortName}"
            values={{
              taxIdShortName: <FormattedMessage {...taxMessageDescriptor} />,
            }}
          />
        </Button>
      </div>
    </form>
  );
};

const taxNumberFormConfig = {
  form: TAX_ID_FORM_NAME,
  validate: taxInformationFormValidation,
};

const vatNumberFormConfig = {
  form: VAT_ID_FORM_NAME,
  validate: taxInformationFormValidation,
};

TaxInformationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  taxInformationId: PropTypes.oneOf(Object.values(TAX_INFORMATION_TYPES)),
  submitError: PropTypes.string,
  setSubmitError: PropTypes.func,
  intl: intlShape,
  // onSubmit is used by redux-forms under the hood
  // Do not call it manually
  onSubmit: PropTypes.func.isRequired,
};

const TaxNumberFormConnected =
  reduxForm(taxNumberFormConfig)(TaxInformationForm);
const VatNumberFormConnected =
  reduxForm(vatNumberFormConfig)(TaxInformationForm);

export {
  TaxNumberFormConnected as TaxNumberForm,
  VatNumberFormConnected as VatNumberForm,
};
